/**
 * Msg 556 — Comparison Card (Slice B).
 *
 * Renders a single entity inside a compare-mode column. Structured
 * bullets, pros/cons, summary, and (when active) a colored match-score
 * badge. Layout is fully self-contained; parent handles column layout.
 *
 * Fields consumed from the card:
 *   • entity_name / title, image_url, url, source_domain
 *   • is_all_inclusive (chip)
 *   • comparison_facts: {
 *       // entity-type specific fields (see FIELD_LABELS)
 *       summary: string,
 *       pros: string[]  (0-3),
 *       cons: string[]  (0-3),
 *     }
 *   • match_score: { score: 0-100, rationale: string } | null
 *   • travelbio_overrides: string[]   (list of field names)
 *   • travelbio_active_promos, travelbio_advisor_tips, ...
 *
 * Callbacks:
 *   • onOpenSavePicker(card)        — existing Save-to-client picker
 *   • onOpenDetail(card)            — opens the full detail drawer
 *   • onAddFact(card, attributeKey) — Slice C hook; safe no-op here
 *   • onCiteFact(card, factField)   — Slice C hook; safe no-op here
 */
import React, { useState } from "react";
import {
  BookOpen,
  Bookmark,
  Check,
  Info,
  Loader2,
  Plus,
  Sparkles,
  Wrench,
  X as CloseIcon,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Human labels for each schema field, grouped by entity_type. The
// keys mirror the backend `_COMPARISON_SCHEMA` in
// atlas_semantic_search_service.py.
const FIELD_LABELS = {
  cruise_ship: {
    gross_tonnage:     "Gross tonnage",
    guest_capacity:    "Guest capacity",
    home_ports:        "Home ports",
    length_or_decks:   "Length / decks",
    notable_features:  "Notable features",
  },
  cruise_line: {
    fleet_size:        "Fleet size",
    target_audience:   "Target audience",
    onboard_style:     "Onboard style",
    price_tier:        "Price tier",
  },
  resort: {
    location:          "Location",
    all_inclusive:     "All-inclusive",
    adults_only:       "Adults-only",
    family_friendly:   "Family-friendly",
    notable_amenities: "Notable amenities",
  },
  hotel: {
    location:          "Location",
    all_inclusive:     "All-inclusive",
    adults_only:       "Adults-only",
    family_friendly:   "Family-friendly",
    notable_amenities: "Notable amenities",
  },
  destination: {
    best_for:                   "Best for",
    climate:                    "Climate",
    flight_time_from_major_hubs: "Flight time",
    signature_experiences:      "Signature experiences",
  },
  other: {
    notable_attributes: "Notable attributes",
  },
};

// Which fields are booleans (render as chip) vs strings/arrays.
const BOOLEAN_FIELDS = new Set([
  "all_inclusive", "adults_only", "family_friendly",
]);

// Msg 556 · C.3 — the search classifier's entity_type enum (`ship`,
// `property`, etc) doesn't line up 1-to-1 with the comparison-facts
// schema. Map aliases so a card with entity_type="ship" still renders
// the cruise_ship spec sheet. The backend now normalizes singletons to
// the side's canonical type; this alias map is defense-in-depth for
// non-compare paths and collection cards.
const ENTITY_TYPE_ALIAS = {
  ship: "cruise_ship",
  cruiseship: "cruise_ship",
  cruise: "cruise_line",
  property: "hotel",
};
function resolveEntityType(t) {
  const raw = String(t || "other").toLowerCase();
  return ENTITY_TYPE_ALIAS[raw] || raw;
}

// Msg 556 · C.3 — generic slugs that should NEVER be the target of a
// structured attribute save. These are containers (a specific ship's
// attribute must NOT land on the "bermuda" or "royal-caribbean" topic).
const _GENERIC_SLUGS = new Set([
  // destinations / regions
  "caribbean", "bahamas", "bermuda", "aruba", "jamaica", "mexico",
  "hawaii", "europe", "asia", "north-america", "south-america",
  "usa", "united-states", "canada", "australia",
  // brands / cruise lines
  "royal-caribbean", "carnival", "norwegian", "msc", "disney-cruise-line",
  "celebrity-cruises", "princess", "holland-america", "cunard",
  // hotel groups
  "hilton", "marriott", "hyatt", "iberostar", "riu", "sandals",
  // generic categories
  "resorts", "hotels", "cruises", "cruise", "resort", "hotel",
  "all-inclusive-resorts", "adults-only-resorts", "boutique-hotels",
  "adventure-travel", "family-resorts", "family-travel",
]);

function slugifyEntity(name) {
  return String(name || "")
    .trim().toLowerCase()
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function pickCanonicalSlug(card) {
  const slugs = Array.isArray(card?.entity_slugs) ? card.entity_slugs : [];
  const nonGeneric = slugs.find((s) => s && !_GENERIC_SLUGS.has(s));
  if (nonGeneric) return nonGeneric;
  return slugifyEntity(card?.entity_name || card?.title || "");
}

function serializeAttrValue(fieldKey, value) {
  if (value == null) return "";
  if (BOOLEAN_FIELDS.has(fieldKey)) {
    return value === true ? "true" : value === false ? "false" : "";
  }
  if (Array.isArray(value)) return value.map((v) => String(v)).join(" | ");
  return String(value).trim();
}

function matchTier(score) {
  if (typeof score !== "number") return null;
  if (score >= 75) return { bg: "bg-emerald-100", ring: "ring-emerald-300", fg: "text-emerald-800" };
  if (score >= 50) return { bg: "bg-amber-100", ring: "ring-amber-300", fg: "text-amber-800" };
  return { bg: "bg-red-100", ring: "ring-red-300", fg: "text-red-800" };
}

function renderValue(value, field) {
  if (BOOLEAN_FIELDS.has(field)) {
    if (value === true) return <span className="font-hand text-emerald-700">✅ Yes</span>;
    if (value === false) return <span className="font-hand text-tb-navy/40">— No</span>;
    return <span className="font-hand text-tb-navy/40">—</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return (
      <ul className="mt-1 space-y-0.5">
        {value.slice(0, 6).map((v, i) => (
          <li key={i} className="font-hand text-[13px] text-tb-navy/85">• {String(v)}</li>
        ))}
      </ul>
    );
  }
  if (value == null || value === "") return null;
  return <span className="font-hand text-tb-navy/85">{String(value)}</span>;
}

export default function ComparisonCard({
  card,
  onOpenSavePicker,
  onOpenDetail,
  onAddFact,
  onCiteFact,
  savedByUrl,
  onSavedAttribute,   // Msg 556 · C.3 — parent hook to refresh card in place
}) {
  const nav = useNavigate();
  const [showRationale, setShowRationale] = useState(false);
  // Msg 556 · C.3 — per-bullet popover state:
  //   savePopover.key: which field is open ("gross_tonnage" etc)
  //   savePopover.mode: "picker" | "conflict"
  //   savePopover.busy: bool
  //   savePopover.existing: {attribute_value, updated_at} when 409
  const [savePopover, setSavePopover] = useState(null);

  if (!card) return null;

  const entityType = resolveEntityType(card.entity_type);
  const labels = FIELD_LABELS[entityType] || FIELD_LABELS.other;
  const facts = card.comparison_facts || {};
  const summary = facts.summary || "";
  const pros = Array.isArray(facts.pros) ? facts.pros : [];
  const cons = Array.isArray(facts.cons) ? facts.cons : [];
  const ms = card.match_score || null;
  const tier = ms ? matchTier(ms.score) : null;
  const overrides = new Set(card.travelbio_overrides || []);
  const image = card.image_url;
  const saved = savedByUrl && card.url ? (savedByUrl[card.url] || []) : [];

  // Which schema fields have values worth rendering (skip null / empty).
  const populatedFields = Object.keys(labels).filter((k) => {
    const v = facts[k];
    if (BOOLEAN_FIELDS.has(k)) return typeof v === "boolean";
    if (Array.isArray(v)) return v.length > 0;
    return v != null && v !== "";
  });

  const canonicalSlug = pickCanonicalSlug(card);

  // Msg 556 · C.3 — direct structured-attribute save (fast path).
  const saveAttribute = async (fieldKey, overwrite = false) => {
    const value = serializeAttrValue(fieldKey, facts[fieldKey]);
    if (!value) {
      toast.error("Cannot save an empty value.");
      return;
    }
    setSavePopover((p) => ({ ...p, busy: true }));
    try {
      if (overwrite) {
        await api.put(`/atlas/topics/${canonicalSlug}/attributes/${fieldKey}`,
                        { attribute_value: value });
      } else {
        await api.post(`/atlas/topics/${canonicalSlug}/attributes`,
                         { attribute_key: fieldKey, attribute_value: value,
                           source: "advisor_typed" });
      }
      toast.success("Saved to TravelBio.");
      setSavePopover(null);
      // Parent refreshes the search so the 🔧 chip appears immediately.
      if (typeof onSavedAttribute === "function") {
        onSavedAttribute({ card, attribute_key: fieldKey });
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.existing) {
        setSavePopover({
          key: fieldKey, mode: "conflict",
          busy: false, existing: detail.existing,
        });
        return;
      }
      if (status === 404) {
        // Topic doesn't exist yet — help the advisor create it via the
        // Knowledge Builder path so they can attach a canonical topic.
        toast.error(`Topic '${canonicalSlug}' not found. Create it via 'Add a fact' first.`);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Save failed.");
      }
      setSavePopover(null);
    }
  };

  // Msg 556 · C.3 — "Edit as narrative fact" opens the Knowledge Builder
  // pre-filled with the entity + factual text so the advisor can refine.
  const openNarrativeEdit = (fieldKey) => {
    const value = serializeAttrValue(fieldKey, facts[fieldKey]);
    const label = (labels[fieldKey] || fieldKey).toString();
    const entity = card.entity_name || card.title || "";
    const text = `${entity} — ${label}: ${value}`;
    const params = new URLSearchParams({
      entity_name: text,
      tags: canonicalSlug,
      display_type: "attribute",
    });
    setSavePopover(null);
    nav(`/atlas/knowledge?${params.toString()}`);
  };

  return (
    <article
      className="relative rounded-2xl border border-tb-navy/15 bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      data-testid={`atlas-compare-card-${card.result_id || card.entity_name}`}
    >
      {/* Match score badge — top-right absolute so it hovers over image */}
      {ms && tier && (
        <button
          type="button"
          onClick={() => setShowRationale((v) => !v)}
          className={`absolute right-3 top-3 z-10 inline-flex items-center gap-1 h-7 px-3 rounded-full ring-1 ${tier.ring} ${tier.bg} ${tier.fg} font-hand text-xs shadow-sm`}
          data-testid={`atlas-compare-match-score-${card.result_id || card.entity_name}`}
          title="Tap to see the rationale"
        >
          <Sparkles className="w-3 h-3" />
          {ms.score}% match
        </button>
      )}

      {/* Optional image header */}
      {image && (
        <div className="relative w-full aspect-[16/9] bg-tb-navy/5 overflow-hidden">
          <img
            src={image}
            alt={card.entity_name || card.title || ""}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}

      <div className="p-4">
        {/* Headline + property chips */}
        <h3
          className="text-tb-navy font-semibold leading-snug"
          style={{ fontFamily: '"Playfair Display", serif', fontSize: "18px" }}
          data-testid={`atlas-compare-entity-name-${card.result_id || card.entity_name}`}
        >
          {card.entity_name || card.title}
        </h3>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {card.source_kind === "travelbio_db" ? (
            <span data-testid={`travelbio-db-badge-${card.result_id || card.entity_name}`}
              title={card.travelbio_last_updated
                ? `Curated from TravelBio · updated ${new Date(card.travelbio_last_updated).toLocaleDateString()}`
                : "Curated from TravelBio"}
              className="inline-flex items-center gap-1 h-5 px-2 rounded-full bg-tb-gold-bright/20 text-tb-navy font-hand text-[10px] ring-1 ring-tb-gold-bright/50">
              <Wrench className="w-2.5 h-2.5" />
              TravelBio
              {card.travelbio_last_updated && (
                <span className="opacity-60">
                  · {new Date(card.travelbio_last_updated).toLocaleDateString()}
                </span>
              )}
            </span>
          ) : card.source_domain && (
            <span className="font-hand text-[11px] text-tb-navy/50">
              {card.source_domain}
            </span>
          )}
          {card.is_all_inclusive && (
            <span className="inline-flex items-center h-5 px-2 rounded-full bg-emerald-50 text-emerald-800 font-hand text-[10px] ring-1 ring-emerald-200">
              all-inclusive
            </span>
          )}
        </div>

        {/* Match score rationale drawer */}
        {ms && showRationale && (
          <div
            className={`mt-3 rounded-lg p-3 ${tier.bg} ring-1 ${tier.ring}`}
            data-testid="atlas-compare-match-rationale"
          >
            <p className={`font-hand text-[12px] ${tier.fg}`}>
              <Info className="w-3 h-3 inline mr-1 -mt-0.5" />
              {ms.rationale}
            </p>
          </div>
        )}

        {/* Summary paragraph */}
        {summary && (
          <p
            className="mt-3 font-hand text-tb-navy/90 text-[13px] leading-relaxed"
            data-testid="atlas-compare-summary"
          >
            {summary}
          </p>
        )}

        {/* Structured facts */}
        {populatedFields.length > 0 && (
          <dl className="mt-3 space-y-2" data-testid="atlas-compare-facts">
            {populatedFields.map((k) => {
              const value = facts[k];
              const isOverridden = overrides.has(k);
              const popoverOpen = savePopover?.key === k;
              return (
                <div key={k}
                     className="grid grid-cols-[110px_1fr] gap-2 items-start group relative">
                  <dt className="font-hand text-[11px] uppercase tracking-wider text-tb-navy/50 mt-0.5">
                    {labels[k]}
                  </dt>
                  <dd className="font-hand text-[13px] text-tb-navy">
                    <div className="flex flex-wrap items-start gap-1.5">
                      {renderValue(value, k)}
                      {isOverridden && (
                        <span
                          className="inline-flex items-center gap-1 h-5 px-2 rounded-full bg-tb-gold-bright/20 text-tb-navy font-hand text-[10px]"
                          title="Overridden by TravelBio curated fact"
                          data-testid={`atlas-compare-override-chip-${k}`}
                        >
                          <Wrench className="w-2.5 h-2.5" />
                          TravelBio
                        </span>
                      )}
                      {/* Msg 556 · C.3 — Save button per bullet. Hidden
                          entirely for fields already curated (override
                          chip present) since the value IS the truth
                          already. On mobile the button is always
                          visible; on desktop it's shown on group hover. */}
                      {!isOverridden && (
                        <button
                          type="button"
                          onClick={() => setSavePopover({ key: k, mode: "picker", busy: false })}
                          data-testid={`atlas-compare-save-bullet-${k}`}
                          title="Save to TravelBio"
                          className="md:opacity-0 md:group-hover:opacity-100 inline-flex items-center gap-0.5 h-5 px-1.5 rounded-full text-tb-navy/60 hover:bg-tb-navy hover:text-tb-gold-bright font-hand text-[10px] transition-opacity"
                        >
                          <Plus className="w-2.5 h-2.5" /> Save
                        </button>
                      )}
                    </div>

                    {/* Popover — picker OR conflict UI. Absolutely
                        positioned under the bullet. */}
                    {popoverOpen && (
                      <div
                        className="mt-2 relative rounded-xl border border-tb-navy/25 bg-white shadow-lg p-3 max-w-md"
                        data-testid={`atlas-compare-save-popover-${k}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-hand text-[11px] uppercase tracking-widest text-tb-navy/60">
                            Save "{labels[k]}"
                          </p>
                          <button
                            type="button"
                            onClick={() => setSavePopover(null)}
                            aria-label="Close"
                            data-testid={`atlas-compare-save-popover-close-${k}`}
                            className="h-6 w-6 -mt-0.5 inline-flex items-center justify-center rounded text-tb-navy/50 hover:bg-tb-navy/5"
                          >
                            <CloseIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="mt-1 font-hand text-[11px] text-tb-navy/60">
                          Topic: <span className="font-medium text-tb-navy">#{canonicalSlug}</span>
                        </p>

                        {savePopover.mode === "conflict" && savePopover.existing ? (
                          <div className="mt-3">
                            <p className="font-hand text-tb-navy text-sm">
                              <b>Already set:</b>{" "}
                              <span className="">{savePopover.existing.attribute_value}</span>
                            </p>
                            <p className="mt-0.5 font-hand text-[11px] text-tb-navy/60">
                              Last updated{" "}
                              {savePopover.existing.updated_at
                                ? new Date(savePopover.existing.updated_at).toLocaleString()
                                : "—"}
                            </p>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => saveAttribute(k, true)}
                                disabled={savePopover.busy}
                                data-testid={`atlas-compare-save-overwrite-${k}`}
                                className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-40"
                              >
                                {savePopover.busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                Overwrite
                              </button>
                              <button
                                type="button"
                                onClick={() => setSavePopover(null)}
                                data-testid={`atlas-compare-save-cancel-${k}`}
                                className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-3 flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={() => saveAttribute(k, false)}
                              disabled={savePopover.busy}
                              data-testid={`atlas-compare-save-structured-${k}`}
                              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-40"
                            >
                              {savePopover.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}
                              Save as structured attribute
                            </button>
                            <button
                              type="button"
                              onClick={() => openNarrativeEdit(k)}
                              data-testid={`atlas-compare-save-narrative-${k}`}
                              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-tb-navy/25 text-tb-navy font-hand text-sm"
                            >
                              <Plus className="w-3.5 h-3.5" />
                              Edit as narrative fact
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}

        {/* Pros — only rendered when non-empty */}
        {pros.length > 0 && (
          <div className="mt-4" data-testid="atlas-compare-pros">
            <p className="font-hand text-[11px] uppercase tracking-widest text-emerald-800 mb-1.5">
              Pros
            </p>
            <ul className="space-y-1">
              {pros.slice(0, 3).map((p, i) => (
                <li key={i} className="flex items-start gap-1.5 font-hand text-[13px] text-tb-navy">
                  <Check className="w-3.5 h-3.5 mt-0.5 text-emerald-700 flex-shrink-0" strokeWidth={3} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Cons — only rendered when non-empty (per phase spec) */}
        {cons.length > 0 && (
          <div className="mt-3" data-testid="atlas-compare-cons">
            <p className="font-hand text-[11px] uppercase tracking-widest text-amber-800 mb-1.5">
              Cons
            </p>
            <ul className="space-y-1">
              {cons.slice(0, 3).map((c, i) => (
                <li key={i} className="flex items-start gap-1.5 font-hand text-[13px] text-tb-navy">
                  <span className="text-amber-700 mt-0.5">⚠</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer CTAs */}
        <div className="mt-4 pt-3 border-t border-tb-navy/10 flex flex-wrap items-center gap-2">
          {onOpenDetail && (
            <button
              type="button"
              onClick={() => onOpenDetail(card)}
              data-testid="atlas-compare-open-detail"
              className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-tb-navy hover:bg-tb-navy hover:text-tb-gold-bright font-hand text-xs border border-tb-navy/20"
            >
              <BookOpen className="w-3 h-3" />
              Details
            </button>
          )}
          {onAddFact && (
            <button
              type="button"
              onClick={() => onAddFact(card)}
              data-testid="atlas-compare-add-fact"
              className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-tb-navy hover:bg-tb-navy hover:text-tb-gold-bright font-hand text-xs border border-tb-navy/20"
            >
              <Plus className="w-3 h-3" />
              Add a fact
            </button>
          )}
          {onOpenSavePicker && (
            <button
              type="button"
              onClick={() => onOpenSavePicker(card)}
              data-testid="atlas-compare-save"
              className={`inline-flex items-center gap-1 h-8 px-3 rounded-full font-hand text-xs ml-auto ${
                saved.length > 0
                  ? "bg-tb-gold-bright/20 text-tb-navy"
                  : "bg-tb-navy text-tb-gold-bright"
              }`}
            >
              <Bookmark className="w-3 h-3" />
              {saved.length > 0 ? `Saved (${saved.length})` : "Save to client"}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
